//----------------------------------------------------------------------------------------------------------------
// Copyright DeerSoft - 2019
//----------------------------------------------------------------------------------------------------------------

import "./util/captureTrainingVoucher";
import React, { Suspense } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import { createRoot, hydrateRoot } from "react-dom/client";
import {
  IsDarkTheme,
  IsElectronContext,
  IsSketchUpContext,
  IsRunningAsBrowser,
  ForceOneWindowRouter,
  kAluvisionRouteTab,
} from "./util/rendererEnvironment";
import Cookies from "js-cookie";
import { getPreferredLanguage } from "./util/browserEnvironment";

import ErrorBoundary from "./electronApp/ErrorBoundary";
import { captureException } from "@sentry/react";
import { Provider } from "react-redux";
import { FetchGlobalSettings } from "./redux/actions/fetch_actions";
import { store } from "./redux/store";
import { ReloadBrowserAfterChunkLoadFailure } from "./util/chunkLoadRecovery";

const LightTheme = React.lazy(() => import("./lighttheme"));
const DarkTheme = React.lazy(() => import("./darktheme"));

const UseDarkTheme = IsDarkTheme() && !IsRunningAsBrowser();

import CONST from "./webApp/CONST";
window.addEventListener("error", ReloadBrowserAfterChunkLoadFailure, true);
window.addEventListener(
  "unhandledrejection",
  ReloadBrowserAfterChunkLoadFailure,
);

const ElectronRouter = React.lazy(() => import("./electronApp/ElectronRouter"));
const VectorworksRouter = React.lazy(
  () => import("./vectorworksApp/VectorworksRouter"),
);
const IosRouter = React.lazy(() => import("./iosApp/iosRouter"));
const WebRouter = React.lazy(() => import("./webApp/WebRouter"));

window.IsSketchup = IsSketchUpContext();
const IsWasmStressTestRoute = new URLSearchParams(window.location.search).has(
  "__wasmStressTest",
);

const InstallWasmStressHarness = () => {
  if (!CONST.DEVELOPMENT && !IsWasmStressTestRoute) {
    return;
  }

  import("./webApp/wasmStressTest")
    .then(({ installWasmStressTest }) => {
      installWasmStressTest();
    })
    .catch((error) =>
      console.error("Unable to install WASM stress harness", error),
    );
};

InstallWasmStressHarness();

//---------------------------------------------------------------------------------
// File Drag and Drop Support

const InstallDragAndDropSupport = () => {
  document.addEventListener("drop", (event) => {
    const files = Array.from(event.dataTransfer.files);
    files.forEach((f, index) => {
      const last = index === event.dataTransfer.files.length - 1;
      const path = window.electron.showFilePath(f).toLocaleLowerCase();
      f.path = path;
      if (path.endsWith(".lrwx")) {
        window.LR_OpenLRWFile({ Path: f.path, Async: true });
      } else if (path.endsWith(".gdtf")) {
        window.LR_ImportGdtf({ Path: f.path, Async: true });
      } else if (path.endsWith(".mvr")) {
        window.LR_ReadMVR({ Path: f.path, Async: true });
      } else if (path.endsWith(".pdf")) {
        window.LR_ReadPDF({ Path: f.path, Async: true });
      } else if (path.endsWith(".stp")) {
        window.LR_ImportDSTV({ Path: f.path, Async: true });
      } else if (path.endsWith(".dxf")) {
        window.LR_ReadDWG({ Path: f.path, Async: true });
      } else if (path.endsWith(".dwg")) {
        window.LR_ReadDWG({ Path: f.path, Async: true });
      } else if (path.endsWith(".svg")) {
        window.LR_ImportSVG({ Path: f.path, Async: true });
      } else if (path.endsWith(".json")) {
        window.LR_Import({ Path: f.path, Async: true });
      } else if (path.endsWith(".ifc")) {
        window.LR_ReadIFC({ Path: f.path, Async: true });
      } else if (path.endsWith(".skp")) {
        window.LR_ReadSketchUp({ Path: f.path, Async: true });
      } else if (
        path.endsWith(".fbx") ||
        path.endsWith(".3ds") ||
        path.endsWith(".dae") ||
        path.endsWith(".obj") ||
        path.endsWith(".glb") ||
        path.endsWith(".gltf")
      ) {
        window.LR_ImportMesh({ Path: f.path, Async: true });
      } else if (
        path.endsWith(".png") ||
        path.endsWith(".jpg") ||
        path.endsWith(".jpeg")
      ) {
        window.LR_ImportTexture({ Path: f.path, Async: true, Last: last });
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    });
  });

  document.addEventListener("dragover", (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
};

const LayerLoader = function ({ component }) {
  FetchGlobalSettings();
  return (
    <Suspense
      fallback={
        <Dimmer active inverted>
          <Loader inverted></Loader>{" "}
        </Dimmer>
      }
    >
      {component}
    </Suspense>
  );
};

if (CONST.COMPILED_FOR_VW) {
  window.IsVectorworksContext = true;
}

window.LR_SHORTCUTS = {};

const InstallShortCutHandling = async () => {
  await import("./component/ShortCutDisplay/ShortCut");

  window.LR_SHORTCUTS = await window.LR_GetShortCutsSettings({});
};

const kConnectorLoadOperation = "LoadConnectors";
const kConnectorNoResponseMessage = "Connector API returned no response";

const SetGlobalConnectors = (dataConnectors, electricalConnectors) => {
  const globalConnectors_Data = {};
  const globalConnectors_Electrical = {};
  const globalConnectors = {};
  dataConnectors.forEach((element) => {
    globalConnectors_Data[element.ConnectorName] = element;
    globalConnectors[element.ConnectorName] = element;
  });
  electricalConnectors.forEach((element) => {
    globalConnectors_Electrical[element.ConnectorName] = element;
    globalConnectors[element.ConnectorName] = element;
  });

  window.globalConnectors = globalConnectors;
  window.globalConnectors_Electrical = globalConnectors_Electrical;
  window.globalConnectors_Data = globalConnectors_Data;
};

const LoadConnectors = async () => {
  let dataConnectors;
  let electricalConnectors;

  try {
    const [dataResponse, electricalResponse] = await Promise.all([
      window.LR_GetConnectors_Data(),
      window.LR_GetConnectors_Electric(),
    ]);

    dataConnectors = Array.isArray(dataResponse?.Connectors)
      ? dataResponse.Connectors
      : [];
    electricalConnectors = Array.isArray(electricalResponse?.Connectors)
      ? electricalResponse.Connectors
      : [];

    if (dataResponse == null || electricalResponse == null) {
      captureException(new Error(kConnectorNoResponseMessage), {
        tags: { operation: kConnectorLoadOperation },
        extra: {
          missingDataResponse: dataResponse == null,
          missingElectricalResponse: electricalResponse == null,
          runtimeState: window.WEB_CORE?.runtimeState,
          lastCommand: window.WEB_CORE?.lastCommand,
        },
      });
    }
  } catch (error) {
    captureException(error, {
      tags: { operation: kConnectorLoadOperation },
      extra: {
        runtimeState: window.WEB_CORE?.runtimeState,
        lastCommand: window.WEB_CORE?.lastCommand,
      },
    });
    SetGlobalConnectors([], []);
    return;
  }

  SetGlobalConnectors(dataConnectors, electricalConnectors);
};

import en from "./localization/raw/en/LightRightObjectsFields.json";
import de from "./localization/raw/de/LightRightObjectsFields.json";

const setLocale = () => {
  window.LR_SetTableViewLocalization({ Locale: { Worksheet: en }, LANG: "EN" });
  window.LR_SetTableViewLocalization({ Locale: { Worksheet: de }, LANG: "DE" });
};

const ThemeChooser = ({ children }) => {
  return (
    <React.Suspense fallback={<></>}>
      {UseDarkTheme ? <DarkTheme /> : <LightTheme />}
      {children}
    </React.Suspense>
  );
};

const WasmStressTestHost = () => {
  return (
    <div
      data-testid="wasm-stress-host"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#101820",
        color: "#f3f4f6",
        fontFamily: "Menlo, Monaco, Consolas, monospace",
        letterSpacing: "0.04em",
      }}
    >
      <div>WASM browser stress test host</div>
    </div>
  );
};

const InstallCheckForUpdate = () => {
  setTimeout(() => {
    window.LR_CheckForUpdate();
  }, 4000);
};

const rootElement = document.getElementById("root");
const prerenderMode = rootElement?.dataset?.prerenderMode;
const prerenderLocale = rootElement?.dataset?.prerenderLocale;
const shouldHydratePrerenderedRoute =
  prerenderMode === "react" &&
  prerenderLocale === getPreferredLanguage() &&
  !IsWasmStressTestRoute;
const kLoadingFadeDurationProperty = "--app-loading-screen-fade-duration";
const kLoadingScreenRemovalSafetyMarginMs = 50;

let root = null;

const getLoadingScreenElement = () =>
  document.getElementById("app-loading-screen");

const getAppLoadingScreenFadeDurationMs = () => {
  const loadingScreenElement = getLoadingScreenElement();
  if (!loadingScreenElement) {
    return 0;
  }

  const durationString = window
    .getComputedStyle(loadingScreenElement)
    .getPropertyValue(kLoadingFadeDurationProperty)
    .trim();
  if (!durationString) {
    return 0;
  }

  const parsedDuration = Number.parseFloat(durationString);
  if (!Number.isFinite(parsedDuration)) {
    return 0;
  }

  if (durationString.endsWith("ms")) {
    return parsedDuration;
  }
  if (durationString.endsWith("s")) {
    return parsedDuration * 1000;
  }
  return 0;
};

const hideAppLoadingScreen = () => {
  const loadingScreenElement = getLoadingScreenElement();
  if (!loadingScreenElement) {
    return;
  }

  if (loadingScreenElement.classList.contains("is-hidden")) {
    return;
  }

  let hasRemovedLoadingScreen = false;
  const removeAppLoadingScreen = () => {
    if (hasRemovedLoadingScreen) {
      return;
    }

    hasRemovedLoadingScreen = true;
    window.clearTimeout(removeAppLoadingScreenTimeout);
    loadingScreenElement.remove();
  };

  const fadeDurationMs = getAppLoadingScreenFadeDurationMs();
  loadingScreenElement.setAttribute("aria-busy", "false");
  const removeAppLoadingScreenTimeout = window.setTimeout(
    removeAppLoadingScreen,
    fadeDurationMs + kLoadingScreenRemovalSafetyMarginMs,
  );
  loadingScreenElement.addEventListener(
    "transitionend",
    removeAppLoadingScreen,
    { once: true },
  );
  loadingScreenElement.classList.add("is-hidden");
};

const hideAppLoadingScreenAfterPaint = () => {
  window.requestAnimationFrame(() =>
    window.requestAnimationFrame(hideAppLoadingScreen),
  );
};

const renderApplication = (component) => {
  if (!rootElement) {
    return;
  }

  if (!root) {
    root = shouldHydratePrerenderedRoute
      ? hydrateRoot(rootElement, component)
      : createRoot(rootElement);
    if (!shouldHydratePrerenderedRoute) {
      root.render(component);
    }
    hideAppLoadingScreenAfterPaint();
    return;
  }

  root.render(component);
  hideAppLoadingScreenAfterPaint();
};

const GetRouter = () => {
  const routeTab = new URLSearchParams(window.location.search).get("tab");
  if (routeTab === kAluvisionRouteTab) {
    return <ElectronRouter />;
  }

  if (
    window.IsSketchup ||
    window.IsVectorworksContext ||
    ForceOneWindowRouter()
  ) {
    return <VectorworksRouter />;
  }
  return <ElectronRouter />;
};

// When the URL Starts with /localview set the window.ElectronWebsocketMode to true

window.LocalVRView = false;
if (window.location.pathname.indexOf("/local-view") !== -1) {
  window.LocalVRView = true;
  window.ElectronWebsocketMode = true;
}

if (window.LocalVRView) {
  InstallDragAndDropSupport();
  import("./util/preload").then(() => {
    import("./util/callback").then(() => {
      FetchGlobalSettings();
      LoadConnectors();
      setLocale();
      InstallShortCutHandling();
      InstallCheckForUpdate();
      renderApplication(
        <ErrorBoundary>
          <ThemeChooser>
            <Provider store={store}>
              <LayerLoader component={GetRouter()} />
            </Provider>
          </ThemeChooser>
        </ErrorBoundary>,
      );
    });
  });
} else if (IsElectronContext()) {
  InstallDragAndDropSupport();
  FetchGlobalSettings();
  LoadConnectors();
  setLocale();
  InstallShortCutHandling();
  renderApplication(
    <ErrorBoundary>
      <ThemeChooser>
        <Provider store={store}>
          <LayerLoader component={GetRouter()} />
        </Provider>
      </ThemeChooser>
    </ErrorBoundary>,
  );
} else if (window.IsSketchup || window.IsVectorworksContext) {
  InstallDragAndDropSupport();
  Promise.all([import("./util/callback"), import("./util/preload")]).then(
    () => {
      FetchGlobalSettings();
      LoadConnectors();
      setLocale();
      InstallShortCutHandling();
      InstallCheckForUpdate();
      renderApplication(
        <ErrorBoundary>
          <ThemeChooser>
            <Provider store={store}>
              <LayerLoader component={GetRouter()} />
            </Provider>
          </ThemeChooser>
        </ErrorBoundary>,
      );
    },
  );
} else if (window.IsIOS || window.Android) {
  import("./util/preload").then(() => {
    FetchGlobalSettings();
    LoadConnectors();
    setLocale();
    InstallShortCutHandling();
    InstallCheckForUpdate();
    renderApplication(
      <ErrorBoundary>
        <ThemeChooser>
          <Provider store={store}>
            <LayerLoader component={<IosRouter />} />
          </Provider>
        </ThemeChooser>
      </ErrorBoundary>,
    );
  });
} else if (IsRunningAsBrowser()) {
  const url = window.location.pathname;

  const IsUsingWebassembly =
    (Cookies.get("access_token") ||
      sessionStorage.getItem("last_sharetoken")) &&
    !url.includes("/Plans") &&
    !url.endsWith("/settings/account/billing") &&
    window.SharedArrayBuffer;

  Promise.all([import("./util/callback"), import("./util/preload")]).then(
    () => {
      FetchGlobalSettings();
      if (IsUsingWebassembly) {
        LoadConnectors();
      }
      renderApplication(
        IsWasmStressTestRoute ? (
          <WasmStressTestHost />
        ) : (
          <ErrorBoundary>
            <ThemeChooser>
              <Provider store={store}>
                <WebRouter />
              </Provider>
            </ThemeChooser>
          </ErrorBoundary>
        ),
      );
    },
  );
} else {
  Promise.all([import("./util/callback"), import("./util/preload")]).then(
    () => {
      FetchGlobalSettings();
      renderApplication(
        <ErrorBoundary>
          <ThemeChooser>
            <Provider store={store}>
              <WebRouter />
            </Provider>
          </ThemeChooser>
        </ErrorBoundary>,
      );
    },
  );
}
